/**
 * DrillSession · дрилл по слабой главе CTFL (slot_s06_rework · W5 · 11 JUN 2026).
 *
 * Короткая сессия из 10 случайных вопросов ОДНОЙ главы — между моками, чтобы
 * добивать слабые места точечно (мокап 04_DESIGN/mockup_s06_hq.html · ack Genie).
 * Источник вопросов: банк «Перевала» (block1-exam) + пул CTFL-моков — у всех
 * вопросов есть istqb-глава. Мгновенный фидбек: ответ → верно/неверно +
 * объяснение → дальше. Эфемерно: в прогресс не пишет (диагностика живёт в моках).
 */
import { useEffect, useMemo, useState } from 'react';
import type { QuizBank, QuizQuestion } from '@shared/schemas';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { useQuizBank } from '@/hooks/useQuizBank';

const DRILL_SIZE = 10;

export const CTFL_CHAPTER_RU: Record<string, string> = {
  'CTFL-Ch1': 'Ch.1 · основы тестирования',
  'CTFL-Ch2': 'Ch.2 · уровни и виды',
  'CTFL-Ch3': 'Ch.3 · статическое тестирование',
  'CTFL-Ch4': 'Ch.4 · техники тест-дизайна',
  'CTFL-Ch5': 'Ch.5 · управление тестированием',
  'CTFL-Ch6': 'Ch.6 · инструменты',
};

function shuffle<T>(arr: T[]): T[] {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j]!, a[i]!];
  }
  return a;
}

/** Все вопросы главы из доступных банков (без дублей по id). */
function chapterPool(chapter: string, banks: ReadonlyArray<QuizBank>): QuizQuestion[] {
  const seen = new Set<string>();
  const pool: QuizQuestion[] = [];
  for (const bank of banks) {
    for (const q of bank.questions) {
      if (q.istqb !== chapter) continue;
      const key = q.question.slice(0, 80);
      if (seen.has(key)) continue;
      seen.add(key);
      pool.push(q);
    }
  }
  return pool;
}

interface DrillSessionProps {
  chapter: string; // 'CTFL-Ch4'
  onClose: () => void;
}

export function DrillSession({ chapter, onClose }: DrillSessionProps) {
  // slot_content_protection (quiz client cutover 29 JUN): the drill pool is built
  // from banks fetched via the gated quiz API (block1-exam + CTFL mock #1), not the
  // bundled banks. Both are full/admin-only — the cockpit drill only renders past
  // S05, so trial users never reach this.
  const { bank: examBank, state: examState } = useQuizBank('block1-exam');
  const { bank: mockBank, state: mockState } = useQuizBank('ctfl-mock-1');
  const banksLoading = examState === 'loading' || mockState === 'loading';
  const questions = useMemo(() => {
    const banks = [examBank, mockBank].filter((b): b is QuizBank => b !== null);
    return shuffle(chapterPool(chapter, banks)).slice(0, DRILL_SIZE);
  }, [chapter, examBank, mockBank]);
  const [idx, setIdx] = useState(0);
  const [picked, setPicked] = useState<string | null>(null);
  const [correctCount, setCorrectCount] = useState(0);
  const [finished, setFinished] = useState(false);

  const trapRef = useFocusTrap<HTMLDivElement>(true);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const q = questions[idx];
  const chapterRu = CTFL_CHAPTER_RU[chapter] ?? chapter;

  const pick = (optId: string) => {
    if (picked !== null || !q) return;
    setPicked(optId);
    if (optId === q.answer) setCorrectCount((c) => c + 1);
  };
  const next = () => {
    setPicked(null);
    if (idx + 1 < questions.length) setIdx(idx + 1);
    else setFinished(true);
  };

  return (
    <>
      <div aria-hidden="true" onClick={onClose} className="fcv-scrim" />
      <div ref={trapRef} role="dialog" aria-modal="true" aria-label={`Дрилл · ${chapterRu}`} className="fcv-modal">
        <header className="fcv-head">
          <h2>Дрилл · {chapterRu}</h2>
          <button type="button" onClick={onClose} aria-label="Закрыть" className="fcv-close">
            ✕
          </button>
        </header>
        <div className="fcv-scroll">
          {banksLoading && (
            <div className="fcv-pane" aria-busy="true">
              <p className="fcv-sub">Готовим вопросы…</p>
            </div>
          )}

          {!banksLoading && questions.length === 0 && (
            <div className="fcv-pane">
              <p className="fcv-sub">
                По этой главе пока нет вопросов в банках. Пройди «Перевал» или Mock #1 — пул
                наполнится.
              </p>
              <button type="button" className="fcv-ghost" onClick={onClose}>
                Закрыть
              </button>
            </div>
          )}

          {!banksLoading && !finished && q && (
            <div className="fcv-pane">
              <div className="fcv-sess-top">
                <span>
                  вопрос {idx + 1} / {questions.length}
                </span>
                <span>верно · {correctCount}</span>
              </div>
              <div className="fcv-bar fcv-sess-bar">
                <div
                  className="fcv-bar-fill"
                  style={{ width: `${Math.round((idx / questions.length) * 100)}%` }}
                />
              </div>
              <h3 className="drx-q" dangerouslySetInnerHTML={{ __html: q.question }} />
              <div className="drx-opts" role="radiogroup">
                {q.options.map((o, i) => {
                  const isPicked = picked === o.id;
                  const revealed = picked !== null;
                  const cls = [
                    'drx-opt',
                    revealed && o.id === q.answer ? 'right' : '',
                    revealed && isPicked && o.id !== q.answer ? 'wrong' : '',
                  ].join(' ');
                  return (
                    <button
                      key={o.id}
                      type="button"
                      className={cls}
                      disabled={revealed}
                      onClick={() => pick(o.id)}
                      role="radio"
                      aria-checked={isPicked}
                    >
                      <span className="mk">{i + 1}</span>
                      <span dangerouslySetInnerHTML={{ __html: o.text }} />
                    </button>
                  );
                })}
              </div>
              {picked !== null && (
                <>
                  <div className={`drx-fb ${picked === q.answer ? 'ok' : 'no'}`}>
                    <b>{picked === q.answer ? 'Верно.' : 'Мимо.'}</b>{' '}
                    <span dangerouslySetInnerHTML={{ __html: q.explanation }} />
                  </div>
                  <button type="button" className="btn-primary fcv-start" onClick={next}>
                    {idx + 1 < questions.length ? 'Дальше →' : 'К итогу'}
                  </button>
                </>
              )}
            </div>
          )}

          {finished && (
            <div className="fcv-pane">
              <h3 className="fcv-fin-title">
                Дрилл закрыт — <span className="fcv-fin-scr">{correctCount} / {questions.length}</span>
              </h3>
              <p className="fcv-fin-sub">
                {correctCount >= Math.ceil(questions.length * 0.8)
                  ? 'Глава держится уверенно — можно идти дальше.'
                  : 'Глава ещё шатается: перечитай разборы и вернись к дриллу завтра.'}
              </p>
              <div className="fcv-fin-ctas">
                <button type="button" className="btn-primary" onClick={() => {
                  setIdx(0);
                  setPicked(null);
                  setCorrectCount(0);
                  setFinished(false);
                }}>
                  Ещё раз →
                </button>
                <button type="button" className="fcv-ghost" onClick={onClose}>
                  Закрыть
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
